import React from "react";
import { Link } from "react-router-dom";
import { Header, Footer, Page } from "@/components/Shell";
import { LOGO_REVELATEUR } from "@/lib/brand";

const Dimension = ({ n, title, desc }) => (
  <div className="border-t border-[color:var(--border-bronze-warm)] pt-6" data-testid={`dim-${n}`}>
    <div className="flex items-baseline gap-4">
      <span className="font-display italic text-bronze-cuivre text-xl">{n}</span>
      <div>
        <div className="font-display text-xl md:text-2xl text-ivory">{title}</div>
        <p className="text-ivory-2 mt-2 text-[15px] leading-relaxed max-w-md">{desc}</p>
      </div>
    </div>
  </div>
);

const Livrable = ({ n, title, desc }) => (
  <div className="border-l border-[color:var(--border-bronze-warm)] pl-6" data-testid={`liv-${n}`}>
    <div className="text-[11px] tracking-[0.32em] uppercase text-bronze-cuivre">{`Livrable ${String(n).padStart(2,"0")}`}</div>
    <div className="font-display text-xl text-ivory mt-2">{title}</div>
    <p className="text-ivory-2 mt-2 text-[15px] leading-relaxed">{desc}</p>
  </div>
);

export default function Home() {
  return (
    <Page tone="dark">
      <Header variant="dark" />

      {/* HERO sombre — emblème à gauche, texte éditorial à droite façon Maison Beaudette™ */}
      <section className="section-dark" data-testid="hero-editorial">
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-20 pb-20 md:pb-28 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          {/* Emblème du Révélateur™ — mis en évidence à gauche */}
          <div className="md:col-span-5 flex justify-center md:justify-start order-1" data-testid="hero-emblem">
            <div className="relative w-full max-w-[460px]">
              <div
                className="absolute inset-0 pointer-events-none"
                aria-hidden="true"
                style={{
                  background: "radial-gradient(circle at 50% 50%, rgba(196,147,97,0.28) 0%, rgba(166,124,82,0.10) 32%, transparent 68%)",
                  filter: "blur(2px)",
                }}
              />
              <img
                src={LOGO_REVELATEUR}
                alt="Emblème Le Révélateur™ par Maison Beaudette™"
                className="relative w-full h-auto select-none"
                draggable={false}
                data-testid="hero-emblem-image"
                style={{
                  filter: "drop-shadow(0 0 32px rgba(196,147,97,0.35)) drop-shadow(0 0 90px rgba(166,124,82,0.18))",
                }}
              />
            </div>
          </div>

          {/* Contenu éditorial */}
          <div className="md:col-span-7 order-2 fade-up">
            <div className="overline-dark">Une lecture stratégique signée Maison Beaudette™</div>
            <h1 className="font-display text-[42px] md:text-[58px] lg:text-[64px] leading-[1.05] text-ivory mt-6 tracking-[-0.005em]">
              Le Révélateur<span className="text-[16px] align-top">™</span>
            </h1>
            <p className="mt-6 text-[19px] md:text-[21px] text-ivory-2 leading-relaxed">
              Une première lecture de votre architecture entrepreneuriale.
            </p>
            <div className="rule-cuivre mt-8 max-w-md" />
            <p className="mt-6 text-[16px] md:text-[17px] text-ivory-2 leading-relaxed">
              Le Révélateur™ met en lumière les fondations, les tensions et les décisions qui influencent
              actuellement la trajectoire de votre entreprise.
            </p>
            <div className="mt-6 flex flex-wrap items-baseline gap-x-6 gap-y-2 text-[11px] tracking-[0.24em] uppercase text-bronze-cuivre">
              <span>Six dimensions examinées</span>
              <span className="text-ivory-2/60">·</span>
              <span>Durée estimée · 10 à 15 min</span>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 items-center">
              <Link to="/commencer" className="btn-primary-on-dark" data-testid="hero-start-btn">Commencer — 49 $</Link>
              <a href="#dimensions" className="btn-ghost btn-ghost-ivory" data-testid="hero-see-btn">Consulter les dimensions</a>
            </div>
            <p className="mt-10 text-[12px] text-ivory-2/60 max-w-lg leading-relaxed">
              Document confidentiel. Vos réponses servent uniquement à établir votre lecture stratégique. Aucun envoi commercial sans votre accord.
            </p>
          </div>
        </div>
      </section>

      {/* POSTULAT — grande citation sur fond noir profond, façon MB™ */}
      <section className="section-dark border-t border-white/5" data-testid="postulat">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-24 md:py-32 text-center">
          <div className="rule-cuivre mx-auto max-w-[80px] mb-8" />
          <div className="overline-dark justify-center inline-flex">Le postulat</div>
          <p className="font-display text-[30px] md:text-[42px] lg:text-[46px] text-ivory leading-[1.22] mt-10 max-w-4xl mx-auto">
            Une entreprise ne se lit pas dans ses résultats. Elle se révèle dans la cohérence de ce qu'elle
            <span className="italic text-bronze-cuivre"> dit</span>, de ce qu'elle
            <span className="italic text-bronze-cuivre"> construit</span> et de ce qu'elle
            <span className="italic text-bronze-cuivre"> permet</span>.
          </p>
        </div>
      </section>

      {/* PROMESSE — retour ivoire pour respirer */}
      <section className="max-w-6xl mx-auto px-5 md:px-10 py-24 grid md:grid-cols-12 gap-14 bg-ivory" data-testid="promise">
        <div className="md:col-span-5">
          <div className="overline mb-4">La démarche</div>
          <h2 className="font-display text-[34px] md:text-[42px] leading-[1.15] text-ink">
            Une lecture stratégique en quelques minutes.
          </h2>
        </div>
        <div className="md:col-span-7 space-y-5 text-[18px] leading-relaxed text-text-2">
          <p>Vous répondez à une série de questions organisées selon la Méthode MB™. Nous mettons en relation les dimensions de votre organisation. Vous recevez une lecture claire de ce qui soutient votre entreprise, de ce qui peut la ralentir et de la décision qui mérite votre attention en premier.</p>
          <p className="text-text-2/80 italic">Pas de score dominant. Pas de graphique tape-à-l'œil. Un document éditorial pensé comme un livrable de direction.</p>
        </div>
      </section>

      {/* DIMENSIONS — sombre à nouveau */}
      <section id="dimensions" className="section-dark">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-24">
          <div className="max-w-3xl mb-14">
            <div className="overline-dark">Dimensions examinées</div>
            <h2 className="font-display text-[36px] md:text-[46px] leading-tight text-ivory mt-6">
              Ce que votre entreprise dit, construit, permet.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10" data-testid="dimensions">
            <Dimension n="I" title="Ce que votre entreprise dit" desc="Votre message, votre valeur, votre promesse et la clarté perçue par vos clients." />
            <Dimension n="II" title="Ce que votre entreprise construit" desc="Vos priorités, vos décisions, vos rôles, votre cohérence interne et votre capacité d'action." />
            <Dimension n="III" title="Ce que votre entreprise permet" desc="L'impact réel de votre entreprise, la confiance créée et la transformation que vos clients comprennent." />
          </div>
        </div>
      </section>

      {/* LIVRABLES — sombre */}
      <section className="section-dark border-t border-white/5" data-testid="deliverables">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-24">
          <div className="max-w-3xl mb-14">
            <div className="overline-dark">Ce que contient votre lecture</div>
            <h2 className="font-display text-[36px] md:text-[46px] leading-tight text-ivory mt-6">
              Un document éditorial personnalisé.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-y-10 gap-x-14">
            <Livrable n={1} title="Une lecture MB™ personnalisée" desc="Une synthèse claire de ce que votre entreprise semble vivre actuellement." />
            <Livrable n={2} title="Trois Révélations Stratégiques™" desc="Les éléments les plus importants à comprendre maintenant." />
            <Livrable n={3} title="Trois Forces Structurantes™" desc="Ce qui soutient déjà votre entreprise." />
            <Livrable n={4} title="Trois Angles Morts™" desc="Ce qui pourrait ralentir votre croissance sans être assez visible." />
            <Livrable n={5} title="Trois Leviers de Croissance™" desc="Les pistes les plus utiles pour avancer avec plus de clarté." />
            <Livrable n={6} title="Votre Priorité Absolue™" desc="La décision qui mérite probablement votre attention en premier." />
          </div>
        </div>
      </section>

      {/* ACHAT — section d'achat, seule autre occurrence du prix */}
      <section className="section-dark border-t border-white/5" data-testid="pricing">
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-24 text-center">
          <div className="overline-dark justify-center inline-flex">Recevoir votre lecture</div>
          <h2 className="font-display text-[38px] md:text-[52px] text-ivory mt-8">
            Rapport de Révélation Stratégique™
          </h2>
          <p className="mt-8 text-[18px] text-ivory-2 leading-relaxed max-w-2xl mx-auto">
            Le Rapport de Révélation Stratégique™ vous donne une première lecture claire de votre entreprise. Il ne remplace pas un accompagnement complet. Il vous aide à voir plus clair, plus vite.
          </p>
          <div className="mt-12 flex justify-center">
            <Link to="/commencer" className="btn-primary-on-dark" data-testid="pricing-cta">Commencer — 49 $</Link>
          </div>
        </div>
      </section>

      <Footer />
    </Page>
  );
}
